import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase/config";
import AIService from "./AIService";
import type { App } from "../types/app";
import type { PSEOTemplateReport, SuggestedPSEOTemplate } from "../types/seo";
import { buildAppContextLines } from "../utils/appContextPrompt";

const REPORTS_COLLECTION = "pseoTemplateReports";

type AIRawTemplate = {
  templateName?: string;
  templatePattern?: string;
  whySuggested?: string;
  appRelevance?: string;
  trafficPotential?: string;
  trafficPotentialReason?: string;
};

type AIRawResponse = {
  summary?: string;
  templates?: AIRawTemplate[];
};

export class SEOService {
  private static generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private static toDate(value: any): Date {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    return new Date(value);
  }

  private static parseJSON<T>(text: string): T | null {
    const raw = String(text || "").trim();
    if (!raw) return null;

    try {
      return JSON.parse(raw) as T;
    } catch {
      // continue
    }

    const fenced =
      raw.match(/```json\s*([\s\S]*?)\s*```/i)?.[1] ||
      raw.match(/```\s*([\s\S]*?)\s*```/i)?.[1];
    if (fenced) {
      try {
        return JSON.parse(fenced) as T;
      } catch {
        // continue
      }
    }

    const start = raw.indexOf("{");
    const end = raw.lastIndexOf("}");
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(raw.slice(start, end + 1)) as T;
      } catch {
        return null;
      }
    }

    return null;
  }

  private static normalizeTemplates(
    input: AIRawTemplate[] | undefined,
  ): SuggestedPSEOTemplate[] {
    if (!Array.isArray(input)) return [];
    return input
      .map((item) => {
        const potentialRaw = String(
          item?.trafficPotential || "medium",
        ).toLowerCase();
        const trafficPotential =
          potentialRaw === "low" || potentialRaw === "high"
            ? potentialRaw
            : "medium";

        return {
          id: this.generateId("pseo_tpl"),
          templateName: String(item?.templateName || "").trim(),
          templatePattern: String(item?.templatePattern || "").trim(),
          whySuggested: String(item?.whySuggested || "").trim(),
          appRelevance: String(item?.appRelevance || "").trim(),
          trafficPotential,
          trafficPotentialReason: String(
            item?.trafficPotentialReason || "",
          ).trim(),
        } as SuggestedPSEOTemplate;
      })
      .filter(
        (item) =>
          item.templateName.length > 0 && item.templatePattern.length > 0,
      );
  }

  private static stripUndefined(value: any): any {
    if (Array.isArray(value))
      return value.map((item) => this.stripUndefined(item));
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([, entry]) => entry !== undefined)
          .map(([key, entry]) => [key, this.stripUndefined(entry)]),
      );
    }
    return value;
  }

  private static buildPrompt(app: App, count: number): string {
    return [
      "You are a programmatic SEO strategist focused on long-term organic growth.",
      `Suggest exactly ${count} high-potential programmatic SEO templates for this app.`,
      "",
      "App context:",
      ...buildAppContextLines(app).map((line) => `- ${line}`),
      "",
      "Requirements for each template:",
      "- templateName: clear human-readable template name.",
      '- templatePattern: scalable pSEO pattern with placeholders, e.g. "Best {use_case} apps for {audience}".',
      "- whySuggested: why this template is strong for long-term traffic.",
      "- appRelevance: how it directly maps to this app.",
      "- trafficPotential: low|medium|high.",
      "- trafficPotentialReason: why the traffic potential level was assigned.",
      "",
      "Output JSON only:",
      '{ "summary": string, "templates": [{ "templateName": string, "templatePattern": string, "whySuggested": string, "appRelevance": string, "trafficPotential": "low|medium|high", "trafficPotentialReason": string }] }',
    ].join("\n");
  }

  static async suggestPSEOTemplates(input: {
    userId: string;
    app: App;
    count: number;
    model?: string;
  }): Promise<PSEOTemplateReport> {
    const safeCount = Math.max(1, Math.min(20, Number(input.count || 5)));
    const response = await AIService.generateText(
      this.buildPrompt(input.app, safeCount),
      {
        model: input.model || undefined,
        useGoogleSearch: true,
        temperature: 0.2,
        maxTokens: 4096,
      },
    );

    const parsed = this.parseJSON<AIRawResponse>(response.text) || {};
    const templates = this.normalizeTemplates(parsed.templates).slice(
      0,
      safeCount,
    );

    while (templates.length < safeCount) {
      templates.push({
        id: this.generateId("pseo_tpl"),
        templateName: `Template ${templates.length + 1}`,
        templatePattern: "{keyword} for {audience}",
        whySuggested:
          "Fallback suggestion generated due incomplete model output.",
        appRelevance: `Relates to ${input.app.name} core audience and use case.`,
        trafficPotential: "medium",
        trafficPotentialReason: "Balanced search intent and scalability.",
      });
    }

    return {
      reportId: this.generateId("pseo_report"),
      userId: input.userId,
      appId: input.app.appId,
      appName: input.app.name,
      modelUsed: input.model || AIService.getDefaultModel(),
      requestedCount: safeCount,
      summary:
        String(parsed.summary || "").trim() ||
        `Generated ${safeCount} pSEO template suggestions for ${input.app.name}.`,
      templates,
      createdAt: new Date(),
    };
  }

  static async saveTemplateReport(report: PSEOTemplateReport): Promise<void> {
    const payload = this.stripUndefined({
      ...report,
      createdAt: report.createdAt || new Date(),
      savedAt: serverTimestamp(),
    });
    await addDoc(collection(db, REPORTS_COLLECTION), payload);
  }

  static async getAppTemplateReports(
    userId: string,
    appId: string,
  ): Promise<PSEOTemplateReport[]> {
    const q = query(
      collection(db, REPORTS_COLLECTION),
      where("userId", "==", userId),
      where("appId", "==", appId),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((item) => {
        const data = item.data() as any;
        return {
          reportId: data.reportId,
          userId: data.userId,
          appId: data.appId,
          appName: data.appName,
          modelUsed: data.modelUsed || AIService.getDefaultModel(),
          requestedCount: Number(data.requestedCount || 0),
          summary: String(data.summary || "").trim(),
          templates: this.normalizeTemplates(data.templates),
          createdAt: this.toDate(data.createdAt),
          savedAt: data.savedAt ? this.toDate(data.savedAt) : undefined,
        } as PSEOTemplateReport;
      })
      .sort(
        (a, b) =>
          (b.savedAt || b.createdAt).getTime() -
          (a.savedAt || a.createdAt).getTime(),
      );
  }

  static async deleteTemplateReport(
    userId: string,
    reportId: string,
  ): Promise<void> {
    const q = query(
      collection(db, REPORTS_COLLECTION),
      where("userId", "==", userId),
      where("reportId", "==", reportId),
    );
    const snapshot = await getDocs(q);
    await Promise.all(snapshot.docs.map((item) => deleteDoc(item.ref)));
  }

  static exportReportAsMarkdown(report: PSEOTemplateReport): string {
    return `# Programmatic SEO Template Report: ${report.appName}

## Summary
${report.summary}

## Templates
${report.templates
  .map(
    (item, index) => `### ${index + 1}. ${item.templateName}
- Pattern: ${item.templatePattern}
- Why suggested: ${item.whySuggested}
- App relevance: ${item.appRelevance}
- Traffic potential: ${item.trafficPotential.toUpperCase()}
- Traffic potential reason: ${item.trafficPotentialReason}`,
  )
  .join("\n\n")}
`.trim();
  }

  static exportReportAsJSON(report: PSEOTemplateReport): string {
    return JSON.stringify(report, null, 2);
  }
}
